import { useMemo } from "react"
import { CollapseElevations } from "components/ui/collapse/CollapseElevations/CollapseElevations"
import { PageIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/PageIcon"
import { ArrowUpRightIcon } from "components/ui/icons/arrowUpRight/ArrowUpRightIcon"
import type { TEcall } from "types/elevationReferences.type"
import style from "./ConnectedCalloutsList.module.scss"

const { calloutList, calloutItem, calloutDot, calloutLabel, calloutRef, collapseLabel, viewButton, viewButtonLabel } = style

type Props = {
	ecalls: TEcall[]
	onEcallClick?: (ecall: TEcall) => void
	showViewButton?: boolean
	/**
	 * When set, each row's reference label is sourced from the elevation_number
	 * whose `eref_id` matches this value. Used by the FurnitureSidebar to surface
	 * the callout's "TYP"-style label relative to the parent eref.
	 */
	referenceLabelErefId?: number | null
}

export const ConnectedCalloutsList = ({
	ecalls,
	onEcallClick,
	showViewButton = false,
	referenceLabelErefId = null,
}: Props) => {
	const groupedByPage = useMemo(
		() =>
			ecalls.reduce<Record<string, TEcall[]>>((acc, ecall) => {
				const key = ecall.location_page_name ?? "Unknown"
				if (!acc[key]) acc[key] = []
				acc[key].push(ecall)
				return acc
			}, {}),
		[ecalls],
	)

	return (
		<>
			{Object.entries(groupedByPage).map(([pageName, pageEcalls]) => (
				<CollapseElevations
					key={pageName}
					items={[
						{
							label: (
								<div className={collapseLabel}>
									<span>
										<PageIcon />
										Page
									</span>
									<span>{pageName}</span>
								</div>
							),
							key: pageName,
							children: (
								<div className={calloutList}>
									{pageEcalls.map((ecall) => {
										const refLabel =
											referenceLabelErefId == null
												? null
												: ecall.elevation_numbers.find((en) => en.eref_id === referenceLabelErefId)?.reference_label ??
												null
										return (
										<div key={ecall.id} className={calloutItem} onClick={() => onEcallClick?.(ecall)}>
											<div className={calloutDot} />
											<span className={calloutLabel}>Callout{ecall.room_name ? ` ${ecall.room_name}` : ""}</span>
											{refLabel ? <span className={calloutRef}>{refLabel}</span> : null}
											{showViewButton ? (
												<button
													type="button"
													className={viewButton}
													onClick={(e) => {
														e.stopPropagation()
														onEcallClick?.(ecall)
													}}
												>
													<span className={viewButtonLabel}>View</span>
													<ArrowUpRightIcon />
												</button>
											) : null}
										</div>
										)
									})}
								</div>
							),
						},
					]}
				/>
			))}
		</>
	)
}
